import { Orama, count, create, insert, search } from "@orama/orama";
import { createWorkerFactory } from "@shopify/web-worker";
import { chain, head, isNil, keyBy, keys, last, sortBy } from "lodash";
import moment from "moment";
import { Constants } from "./components/shared/Constants";

const createLoadDBWorker = createWorkerFactory(
  () => import("./workers/LoadDBWorker")
);

const getRandomInt = (min: number, max: number): number => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export type Track = {
  name: string;
  format: string;
  length: string;
  title: string;
  track: string;
};

export type Entry = {
  identifier: string;
  title: string;
  date: string;
  subject: string;
  venue: string;
  coverage: string;
  source: string;
  transferer: string;
  hosts: string[];
  directory: string;
  isSoundboard: boolean;
  image?: EntryImageType;
  tracks: Track[];
  artist?: string;
};

export type EntryImageType = {
  url: string;
  width: number;
  height: number;
};

export type Chords = {
  identifier: string;
  title: string;
  artist: string;
  html: string;
};

export type ChunkConfig = {
  chunks: number;
  entriesLength: number;
  yearStart: number;
  yearEnd: number;
  dateEnd: string;
};

class GratefulDB {
  indexByYear: { [year: string]: Orama };
  entriesById: { [id: string]: Entry };
  entriesByYear: { [year: string]: Entry[] };
  top10ByYear: { [year: string]: Entry[] | undefined };
  chordsById: { [id: string]: Chords };
  timeMachineMap: {
    [year: string]: { entry: Entry; dateParsed: moment.Moment }[];
  };
  chordsIndex: Orama | undefined;
  config: ChunkConfig;
  initialized: boolean;

  constructor() {
    this.initialized = false;
    this.indexByYear = {};
    this.entriesById = {};
    this.top10ByYear = {};
    this.chordsById = {};
    this.timeMachineMap = {};
    this.entriesByYear = {};
    this.config = {
      yearStart: 0,
      yearEnd: 0,
      chunks: 0,
      entriesLength: 0,
      dateEnd: "",
    };
  }

  init(
    loading: {
      setLoaded: () => void;
    },
    setChunkConfig: (config: ChunkConfig) => void,
    setChunkLoaded: () => void
  ): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    const _this = this;
    createLoadDBWorker().load(
      this.indexByYear,
      this.entriesById,
      (config: ChunkConfig) => {
        _this.config = config;
        setChunkConfig(config);
      },
      () => {
        _this.populateEntriesByYear();
        _this.populateTop10ByYear();
        _this.populateTimeMachineMap();
        _this.populateChords().then(async () => {
          const searchIndexLength = await count(
            _this.indexByYear[Constants.EmptyString]
          );
          console.info(
            `[GratefulDB] ChunkConfig: ${this.config?.entriesLength}`
          );
          console.info(
            `[GratefulDB] Entries: ${Object.keys(_this.entriesById).length}`
          );
          console.info(`[GratefulDB] Search Index: ${searchIndexLength}`);
          loading.setLoaded();
        });
      },
      setChunkLoaded
    );
  }

  get(id: string): Entry {
    return this.entriesById[id];
  }

  chunkConfig(): ChunkConfig {
    return this.config;
  }

  listByYear(year: string): Entry[] {
    const entries = this.entriesByYear[year];
    if (!entries) {
      return [];
    }
    return sortBy(
      entries,
      (e) => Date.parse(e.date),
      (e) => !e.isSoundboard
    );
  }

  async search(term: string, year?: string): Promise<Entry[]> {
    const _this = this;

    const results = await search(
      this.indexByYear[year || Constants.EmptyString],
      {
        term,
        limit: 100,
      }
    );

    if (results.count === 0) {
      return [];
    }

    return chain(results)
      .get("hits")
      .map("id")
      .uniq()
      .map((id) => _this.get(id as string))
      .sortBy(
        (e) => Date.parse(e.date),
        (e) => !e.isSoundboard
      )
      .value();
  }

  populateEntriesByYear(): void {
    this.entriesByYear = chain(this.entriesById)
      .values()
      .groupBy((entry) => entry.date.substring(0, 4))
      .mapValues((entryList) => sortBy(entryList, "date"))
      .value();
  }

  populateTop10ByYear(): void {
    const take = 3;
    this.top10ByYear = chain(this.entriesById)
      .values()
      .filter((entry) => entry.isSoundboard)
      .filter((entry) => entry.date.substring(5) !== "00-00")
      .groupBy((entry) => entry.date.substring(0, 4))
      .mapValues((entryList) => {
        const top = chain(entryList)
          .sortBy("date")
          .uniqBy("date")
          .take(take)
          .value();
        if (top.length < take) {
          return undefined;
        }
        return top;
      })
      .omitBy(isNil)
      .value();
  }

  populateTimeMachineMap(): void {
    this.timeMachineMap = chain(this.entriesById)
      .filter((entry) => entry.date.substring(5) !== "00-00")
      .groupBy((entry) => entry.date.substring(0, 4))
      .mapValues((entryList) =>
        chain(entryList)
          .groupBy((entry) => entry.date)
          .mapValues((entryList) =>
            chain(entryList).orderBy(["isSoundboard"], ["desc"]).head().value()
          )
          .flatMap()
          .value()
      )
      .mapValues((entryList) =>
        chain(entryList)
          .sortBy("date")
          .map((entry) => {
            return {
              entry,
              dateParsed: moment(entry.date, "YYYY-MM-DD"),
            };
          })
          .value()
      )
      .value();
  }

  randomTimeMachineEntry(): Entry | undefined {
    const tmKeys = keys(this.timeMachineMap);
    const startYear = head(tmKeys);
    const endYear = last(tmKeys);
    if (!startYear || !endYear) {
      return;
    }
    return this.findClosestShow(
      getRandomInt(parseInt(startYear), parseInt(endYear)),
      getRandomInt(1, 12),
      getRandomInt(1, 31)
    );
  }

  randomRadioEntry(startYear: number, endYear: number): Entry | undefined {
    return this.findClosestShow(
      getRandomInt(startYear, endYear),
      getRandomInt(1, 12),
      getRandomInt(1, 31)
    );
  }

  findClosestShow(year: number, month: number, day: number): Entry | undefined {
    if (!this.timeMachineMap[year]) {
      return;
    }
    const date = moment(`${year}-${month}-${day}`, "YYYY-MM-DD");
    const shows = this.timeMachineMap[year];
    for (let i = 0; i < shows.length; i++) {
      const show = shows[i];
      if (show.dateParsed >= date) {
        return show.entry;
      }
    }
  }

  getTop10ByYear(): { [year: string]: Entry[] | undefined } {
    return this.top10ByYear;
  }

  async populateChords(): Promise<unknown> {
    const _this = this;
    this.chordsIndex = await create({
      schema: {
        title: "string",
      },
    });

    return fetch(`/${process.env.REACT_APP_GRATEFUL_CHORDS_NAME}.json`, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((response) => response.json())
      .then((entries) => {
        _this.chordsById = keyBy(entries, "identifier");
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          if (this.chordsIndex) {
            insert(this.chordsIndex, {
              id: entry.identifier,
              title: entry.title,
            });
          }
        }
      });
  }

  getChords(identifier: string): Chords {
    return this.chordsById[identifier];
  }

  async searchChords(searchStr: string): Promise<Chords[]> {
    const _this = this;

    if (!this.chordsIndex) {
      return [];
    }

    const results = await search(this.chordsIndex, {
      term: searchStr,
      limit: 3,
    });

    if (results.count === 0) {
      return [];
    }

    return chain(results)
      .get("hits")
      .map("id")
      .uniq()
      .map((id) => _this.getChords(id as string))
      .value();
  }

  getChordsByLetter(): { [letter: string]: Chords[] } {
    return chain(this.chordsById)
      .values()
      .groupBy((entry) => entry.title.substring(0, 1).toUpperCase())
      .mapValues((entryList) => sortBy(entryList, "title"))
      .value();
  }
}

export default GratefulDB;
